import { TFireEvent } from "@/modules/irohana-components/types/common";
import { v4 as uuidv4 } from "uuid";
import Moji from "moji";
import { FormEvent } from "react";
import { dayjs } from "@/modules/irohana-components/utils/dayjs";
import { Dayjs } from "dayjs";

/**
 * Generates a new UUID (Universally Unique Identifier).
 *
 * @returns {string} - The generated UUID.
 */
export const makeUuid = (): string => {
  return uuidv4();
};

/**
 * Converts a hex color code to an RGBA color string.
 *
 * @param {string} hexColor - The hex color code.
 * @param {number} alpha - The alpha (transparency) value (0 to 1).
 * @returns {string} - The RGBA color string.
 */
export const hexToRgba = (hexColor: string, alpha: number): string => {
  hexColor = hexColor.replace("#", "");
  // Convert hexadecimal to RGB
  const red = parseInt(hexColor.substring(0, 2), 16);
  const green = parseInt(hexColor.substring(2, 4), 16);
  const blue = parseInt(hexColor.substring(4, 6), 16);

  // Validate alpha value
  alpha = Math.min(1, Math.max(0, alpha));

  // Format and return RGBA string
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

/**
 * Removes duplicate elements from an array.
 *
 * @template T
 * @param {T[]} arr - The array to process.
 * @returns {T[]} - A new array with unique elements.
 */
export const uniqueArray = <T>(arr: T[]): T[] => {
  return Array.from(new Set(arr));
};

/**
 * Pauses execution for a specified number of milliseconds.
 *
 * @param {number} ms - The number of milliseconds to sleep.
 * @returns {Promise<void>} - A promise that resolves after the specified time.
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

/**
 * Validates and returns the cleaned form values based on a given schema.
 *
 * @template T
 * @param {T} values - The form values to validate.
 * @param {ObjectSchema<object>} schema - The validation schema.
 * @returns {Promise<Partial<T>>} - A promise that resolves with the validated form values.
 * @throws {Error} - Throws an error if validation fails.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFormValues = async <T extends object>(values: T, schema: any): Promise<Partial<T>> => {
  try {
    const data = await schema.cast(values);
    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Extracts and returns text content from an HTML string.
 *
 * @param {string} html - The HTML string to extract text from.
 * @returns {string} - The extracted text content.
 */
export const extractTextFromHTML = (html: string): string => {
  const span = document.createElement("span");
  span.innerHTML = html;
  span.remove();
  return span.textContent || span.innerText;
};

export const isFireEvent = <T>(event: Event): event is TFireEvent<T> => {
  if ("detail" in event) return true;
  return false;
};

export const convertHalfWidthToFullWidth = (text: string): string => {
  if (!text) return text;

  // Replace problematic Unicode characters with CP932-compatible equivalents
  // Mac vs Windows Unicode mapping inconsistencies:
  // - Wave dash: 〜 (U+301C) → ～ (U+FF5E)
  // - Minus sign: − (U+2212) → － (U+FF0D)
  // - Double vertical line: ‖ (U+2016) → ∥ (U+2225)
  // - Cent sign: ¢ (U+00A2) → ￠ (U+FFE0)
  // - Pound sign: £ (U+00A3) → ￡ (U+FFE1)
  // - Not sign: ¬ (U+00AC) → ￢ (U+FFE2)
  const normalized = text.replace(/〜/g, "～").replace(/−/g, "－").replace(/‖/g, "∥").replace(/¢/g, "￠").replace(/£/g, "￡").replace(/¬/g, "￢");

  const fullWidth = Moji(normalized)
    .convert("HE", "ZE") // alphanumeric (a-z, 0-9) to full-width
    .convert("HK", "ZK") // half-width Katakana to full-width Katakana
    .toString()
    .replace(/ /g, "　");

  return fullWidth;
};

export const convertFullWidthToHalfWidth = (text: string): string => {
  if (!text) return text;

  const halfWidth = Moji(text)
    .convert("ZE", "HE") // full-width alphanumeric (A-Z, 0-9) to half-width
    .convert("ZK", "HK") // full-width Katakana to half-width Katakana
    .toString()
    .replace(/　/g, " ");

  return halfWidth;
};

/**
 * Capture-phase input handler that converts full-width characters typed into an
 * INPUT element to their half-width equivalents in place, while preserving the
 * caret/selection position.
 *
 * Uses the native HTMLInputElement `value` setter so React's synthetic event
 * system still observes the change and downstream `onChange` handlers fire with
 * the converted value.
 *
 * @param {FormEvent<HTMLElement>} e - The input form event (attach via `onInputCapture`).
 * @returns {void}
 */
export const handleFullwidthInputCapture = (e: FormEvent<HTMLElement>): void => {
  const target = e.target as HTMLInputElement | null;
  if (!target || target.tagName !== "INPUT") return;
  const current = target.value;
  const converted = convertFullWidthToHalfWidth(current);
  if (converted === current) return;
  const selectionStart = target.selectionStart;
  const selectionEnd = target.selectionEnd;
  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
  if (nativeSetter) nativeSetter.call(target, converted);
  else target.value = converted;
  if (selectionStart !== null && selectionEnd !== null) {
    try {
      target.setSelectionRange(selectionStart, selectionEnd);
    } catch {
      /* selection not supported on this input type */
    }
  }
};

// Convert full-width to half-width but preserve specific Katakana characters (e.g. ユ for license numbers)
export const convertFullWidthToHalfWidthForMatchLicenseNumber = (text: string): string => {
  if (!text) return text;

  // Preserve the character ユ before conversion
  const preservedChar = text.replace(/ユ/g, "___PRESERVED_YU___");

  const halfWidth = Moji(preservedChar)
    .convert("ZE", "HE") // full-width alphanumeric (A-Z, 0-9) to half-width
    .convert("ZK", "HK") // full-width Katakana to half-width Katakana
    .toString()
    .replace(/　/g, " ")
    .replace(/___PRESERVED_YU___/g, "ユ");

  return halfWidth;
};

// Convert Katakana to Hiragana
export const convertKanaToHiragana = (text: string): string => {
  return text.normalize("NFKC").replace(/[\u30A1-\u30FA]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60));
};

// Sanitize input: normalize whitespace, collapse spaces, trim
export const sanitizeInput = (text: string): string => {
  return text
    .replace(/\u3000/g, " ")
    .replace(/\t+/g, " ")
    .replace(/ {2,}/g, " ")
    .replace(/\r\n|\r/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
};

export const getStartDateOfYear = (resource: { year: number }): Dayjs => {
  const { year } = resource;
  const startMonth = 3;

  return dayjs(new Date(year, startMonth, 1));
};

export const getEndDateOfYear = (resource: { year: number }): Dayjs => {
  const { year } = resource;
  const endMonth = 2;

  return dayjs(new Date(year + 1, endMonth, 31));
};
